<script lang="ts">
	/**
	 * The six boxes a one-time code is typed into (Design §14).
	 *
	 * Ported from AbstractID's own `OTPInput`, deliberately look-alike: a customer who has signed
	 * in to any other Abstract product has already used this control, and a code entry that looks
	 * different from the one the email came from reads as a different site asking for the code.
	 *
	 * Three behaviours are what make it worth six inputs rather than one:
	 *   - typing advances, backspace on an empty box retreats;
	 *   - a **paste anywhere** distributes across all six, because the code arrives by email and
	 *     copying it whole is what everybody does — a paste that fills only the first box makes
	 *     the control worse than a plain text field;
	 *   - the last digit submits, so the code is never typed and then left sitting there.
	 *
	 * `disabled` is bound to the verification being in flight rather than to nothing: without it a
	 * second keystroke while the first submission is open fires `complete` again with the same
	 * code, and the second attempt fails against an OTP the first one has already consumed.
	 */

	import { createEventDispatcher } from 'svelte';
	import { Helper } from 'flowbite-svelte';

	/** Red border plus the message underneath. Pass a space for the border alone. */
	export let error: string = '';
	/** Blocks input while a code is being verified — see the note about double submission above. */
	export let disabled: boolean = false;

	const LENGTH = 6;

	const dispatcher = createEventDispatcher<{ complete: string }>();

	let digits: string[] = Array(LENGTH).fill('');
	let inputs: HTMLInputElement[] = [];

	/** Clears every box and returns the caret to the first, after a code was refused. */
	export function reset(): void {
		digits = Array(LENGTH).fill('');
		inputs[0]?.focus();
	}

	/** Puts the caret in the first box, so the panel can hand over focus on arrival. */
	export function focus(): void {
		inputs[0]?.focus();
	}

	function fnDispatchWhenFull(): void {
		if (digits.every(digit => digit.length === 1)) dispatcher('complete', digits.join(''));
	}

	function fnInput(index: number, event: Event): void {
		const target = event.target as HTMLInputElement;
		// Only the last digit typed: an autofilled or over-typed box would otherwise hold two.
		const value = target.value.replace(/\D/g, '').slice(-1);

		digits[index] = value;
		// The DOM value has to be written back — a non-digit keystroke leaves the box holding a
		// character Svelte has no reason to re-render away, since `digits[index]` never changed.
		target.value = value;

		if (value.length > 0 && index < LENGTH - 1) inputs[index + 1]?.focus();

		fnDispatchWhenFull();
	}

	function fnKeydown(index: number, event: KeyboardEvent): void {
		if (event.key === 'Backspace' && digits[index].length === 0 && index > 0) {
			inputs[index - 1]?.focus();
		}
	}

	/**
	 * A paste of the whole code, caught on the row rather than per box.
	 *
	 * Non-digits are stripped before the split, so a code copied with the surrounding words of the
	 * email — or with a stray space — still lands correctly.
	 */
	function fnPaste(event: ClipboardEvent): void {
		event.preventDefault();

		const pasted = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '').slice(0, LENGTH);
		digits = Array.from({ length: LENGTH }, (_unused, index) => pasted[index] ?? '');

		inputs[Math.min(pasted.length, LENGTH - 1)]?.focus();

		fnDispatchWhenFull();
	}
</script>

<div class="mb-4">
	<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
	<div class="flex justify-center gap-2" on:paste={fnPaste}>
		{#each digits as digit, index (index)}
			<input
				bind:this={inputs[index]}
				type="text"
				inputmode="numeric"
				autocomplete={index === 0 ? 'one-time-code' : 'off'}
				maxlength="1"
				{disabled}
				aria-label={`Digit ${index + 1} of ${LENGTH}`}
				value={digit}
				class="h-14 w-12 rounded-lg border text-center font-mono text-2xl bg-depth-current disabled:opacity-60
					{error.length > 0
						? 'border-red-500 focus:border-red-500 focus:ring-red-500'
						: 'border-widget focus:border-primary-500 focus:ring-primary-500'}"
				on:input={event => fnInput(index, event)}
				on:keydown={event => fnKeydown(index, event)}
			/>
		{/each}
	</div>

	{#if error.trim().length > 0}
		<Helper class="mt-2 text-center" color="red">{error}</Helper>
	{/if}
</div>
